import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { API_BASE_URL } from '@/config';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FileProcessingErrorModal from '@/components/FileProcessingErrorModal.jsx/FileProcessingErrorModal';
import { useConfirmation } from '@/context/ConfirmationContext';
import { getOrganizations } from '@/views/Organizations/Organizations.api';
import { utcToLocal } from '@/utility';
import { DeleteForeverRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import moment from 'moment';
import useRefundFilesWithSummary from '../../utils/hooks/refund-files/useRefundFilesWithSummary';

export const REFUND_FILE_PROCESS_STATUSES = {
  0: 'Unprocessed',
  1: 'Processed',
  2: 'Canceled',
};

const UploadedFiles = () => {
  const { t } = useTranslation();

  const getStatusLabel = (statusCode) => {
    switch (statusCode) {
      case 0:
        return t('uploadedFiles.statuses.unprocessed');
      case 1:
        return t('uploadedFiles.statuses.processed');
      case 2:
        return t('uploadedFiles.statuses.canceled');
      default:
        return REFUND_FILE_PROCESS_STATUSES[statusCode] || '-';
    }
  };

  const [files, setFiles] = useState([]);
  const [fileStatus, setFileStatus] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [organizationId, setOrganizationId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [fileProcessingErrors, setFileProcessingErrors] = useState(null);

  const [fileMetadata, setFileMetadata] = useState(null);
  const filesWithRefundSummary = useRefundFilesWithSummary(files);
  const { requestConfirmation } = useConfirmation();
  const filteredFacilities = organizationId
    ? allFacilities.filter(
        (facility) => facility.organization_id === parseInt(organizationId),
      )
    : allFacilities;

  const selectOrganization = (event) => {
    setOrganizationId(event.target.value);
    setFacilityId('');
  };

  const selectFacility = (event) => {
    setFacilityId(event.target.value);
  };

  const handleSelectFileStatus = (event) => {
    setFileStatus(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFiles = async () => {
    const filesList = await axios.post(API_BASE_URL + '/adminGetFilesList', {
      organizationId: organizationId,
      facilityId: facilityId,
      fileStatus,
    });
    setFiles(filesList.data);
  };

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        getFiles();
      } catch (err) {
        console.log(err);
      }
    };

    fetchFiles();
  }, [organizationId, facilityId, fileStatus]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await getOrganizations();
        setOrganizations(response.data.organizations);
      } catch (err) {
        console.log(err);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await axios.get(API_BASE_URL + '/facilitiesPro');
        setAllFacilities(response.data);
      } catch (err) {
        console.log(err);
      }
    };

    fetchFacilities();
  }, []);

  const processFile = async (fileId) => {
    requestConfirmation(
      t('uploadedFiles.confirmations.processFile'),
      async () => {
        try {
          await axios.get(API_BASE_URL + '/processFileById/' + fileId);
          getFiles().catch();
        } catch (err) {
          console.log(err);
          if (
            err.response.status === 400 &&
            err.response.data.processingErrors
          ) {
            console.log(err.response.data.processingErrors);
            setFileProcessingErrors(err.response.data.processingErrors);

            setFileMetadata({ fileId });
          }
        }
      },
    );
  };

  const validateFile = async (fileId) => {
    try {
      await axios.get(
        API_BASE_URL + '/processFileById/' + fileId + '?validate=true',
      );
    } catch (err) {
      if (err.response.status === 400 && err.response.data.processingErrors) {
        setFileProcessingErrors(err.response.data.processingErrors);
        setFileMetadata({ fileId });
      }
    }
  };

  const handleCancelClick = (fileId, fileName) => {
    requestConfirmation(
      t('uploadedFiles.confirmations.cancelFile', { fileName }),
      (comment) => cancelFile(fileId, comment),
      null,
      {
        showCommentField: true,
        commentLabel: t('uploadedFiles.commentLabel'),
        commentPlaceholder: t('uploadedFiles.commentPlaceholder'),
        commentRequired: true,
      },
    );
  };

  const deleteCsvFile = async (id) => {
    requestConfirmation(
      t('uploadedFiles.confirmations.deleteFile'),
      async () => {
        try {
          await axios.delete(API_BASE_URL + '/deleteCsvFile/' + id);
          getFiles().catch();
        } catch (err) {
          console.log(err);
        }
      },
    );
  };

  const clearErrors = () => {
    setFileProcessingErrors(null);
  };
  const cancelFile = async (fileId, comment) => {
    try {
      await axios.post(API_BASE_URL + '/refund-files/' + fileId + '/cancel', {
        comment,
      });
      getFiles().catch((err) => console.log(err));
    } catch (err) {
      console.log(err);
    }
  };

  const forceProcessFile = async (fileId) => {
    try {
      await axios.get(
        `${API_BASE_URL}/processFileById/${fileId}?force_process=true`,
      );
      setFileProcessingErrors(null);
      getFiles().catch();
    } catch (err) {
      console.log(err);
    }
  };

  const getRowStyles = (row) => {
    if (row.process_status === 2) {
      return {
        backgroundColor: '#FFFACD',
      };
    }
    return {};
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('uploadedFiles.title')}</h4>
          </CardHeader>
          <CardBody>
            <div>
              <div className='t-header'>
                <div className='t-header-right'>
                  <div className='filter-container'>
                    <div className='custom-select'>
                      <select
                        className='full-width-elem'
                        id='File Status'
                        onChange={handleSelectFileStatus}>
                        <option value='' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.fileStatusPlaceholder')}
                        </option>
                        <option value='0' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.unprocessed')}
                        </option>
                        <option value='1' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.processed')}
                        </option>
                        <option value='2' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.canceled')}
                        </option>
                      </select>
                    </div>
                    <div className='custom-select'>
                      <select
                        className='full-width-elem'
                        id='Organization'
                        onChange={selectOrganization}>
                        <option value='' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.organizationsPlaceholder')}
                        </option>
                        {organizations.map((item) => {
                          return (
                            <React.Fragment key={item.id}>
                              <option
                                value={item.id}
                                className='parent'
                                style={{ fontWeight: 'bold' }}
                                key={item.id}>
                                {item.name}
                              </option>
                            </React.Fragment>
                          );
                        })}
                      </select>
                    </div>
                    <div className='custom-select'>
                      <select
                        className='full-width-elem'
                        id='Facility'
                        onChange={selectFacility}>
                        <option value='' style={{ fontWeight: 'bold' }}>
                          {t('uploadedFiles.filters.facilitiesPlaceholder')}
                        </option>
                        {filteredFacilities.map((item) => {
                          return (
                            <React.Fragment key={item.id}>
                              <option
                                value={item.id}
                                className='parent'
                                style={{ fontWeight: 'bold' }}
                                key={item.id}>
                                {item.name}
                              </option>
                            </React.Fragment>
                          );
                        })}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <Table id='template'>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.fileUploadedDate')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.facilityName')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.organizationName')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.fileStatus')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.uploadedBy')}
                      </TableCell>
                      <TableCell>{t('uploadedFiles.table.fileName')}</TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.recordCount')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.totalAmount')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.dataMappingName')}
                      </TableCell>
                      <TableCell>
                        {t('uploadedFiles.table.processedDate')}
                      </TableCell>
                      <TableCell width='5%'></TableCell>
                    </TableRow>
                  </TableHead>
                  {filesWithRefundSummary && files.length > 0 ? (
                    <TableBody>
                      {(rowsPerPage > 0
                        ? filesWithRefundSummary.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                        : filesWithRefundSummary
                      ).map((obj, index) => (
                        <TableRow
                          className='txtalign'
                          key={index}
                          style={getRowStyles(obj)}>
                          <TableCell width='15%'>
                            <div
                              style={{
                                display: 'flex',
                                gap: '10px',
                              }}>
                              <Button
                                onClick={() => validateFile(obj.id)}
                                color='primary'
                                disabled={
                                  obj.process_status === 1 ||
                                  obj.process_status === 2
                                }>
                                {t('uploadedFiles.buttons.validate')}
                              </Button>
                              <Button
                                onClick={processFile.bind(null, obj.id)}
                                color='primary'
                                disabled={
                                  obj.process_status === 1 ||
                                  obj.process_status === 2
                                }>
                                {t('uploadedFiles.buttons.processFiles')}
                              </Button>
                              <Button
                                onClick={() =>
                                  handleCancelClick(obj.id, obj.filename)
                                }
                                color='danger'
                                disabled={
                                  obj.process_status === 0 ||
                                  obj.process_status === 2
                                }>
                                {t('uploadedFiles.buttons.cancel')}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            {obj.created_at
                              ? utcToLocal(obj.created_at, 'MM/DD/YYYY')
                              : '-'}
                          </TableCell>
                          <TableCell>{obj && obj.name}</TableCell>
                          <TableCell>
                            {obj && obj.organization_name
                              ? obj.organization_name
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {getStatusLabel(obj.process_status)}
                          </TableCell>
                          <TableCell>{obj.uploaded_by}</TableCell>

                          <TableCell>
                            <span title={obj.filename}>
                              <Typography fontSize={14}>
                                {obj.filename}
                              </Typography>
                            </span>
                          </TableCell>
                          <TableCell width={200}>
                            {obj.process_status !== 0 ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '2px',
                                }}>
                                {Object.keys(obj.refund_summary).map((key) => {
                                  return obj.refund_summary[key] ? (
                                    <Typography fontSize={'14px'} key={key}>
                                      {key}: {obj.refund_summary[key]}
                                    </Typography>
                                  ) : null;
                                })}
                                <Typography fontSize={'14px'}>
                                  {t('uploadedFiles.table.total')}{' '}
                                  {obj.total_records}
                                </Typography>
                              </div>
                            ) : obj.total_records == 0 ? (
                              t('uploadedFiles.table.emptyFile')
                            ) : (
                              obj.total_records
                            )}
                          </TableCell>
                          <TableCell>
                            {obj.total_amount == 0
                              ? t('uploadedFiles.table.emptyFile')
                              : obj.total_amount}
                          </TableCell>
                          <TableCell>
                            {obj.data_mapping_group_name
                              ? obj.data_mapping_group_name
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {obj.processed_at
                              ? moment(obj.processed_at).format('MM-DD-YY')
                              : '-'}
                          </TableCell>
                          <TableCell width='5%'>
                            <a
                              href={
                                API_BASE_URL +
                                '/getFile?filePath=' +
                                obj.file_location
                              }>
                              <div>
                                <CloudDownloadIcon />
                              </div>
                            </a>
                            {obj.process_status !== 1 && (
                              <a onClick={() => deleteCsvFile(obj.id)}>
                                <div>
                                  <DeleteForeverRounded />
                                </div>
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('uploadedFiles.table.noTransactionData')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[
                          25,
                          50,
                          { label: t('achFiles.pagination.all'), value: -1 },
                        ]}
                        colSpan={3}
                        count={files.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        labelRowsPerPage={t('achFiles.pagination.rowsPerPage')}
                        SelectProps={{
                          inputProps: {
                            'aria-label': t('achFiles.pagination.rowsPerPage'),
                          },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </div>
          </CardBody>
        </Card>
      </GridItem>
      <FileProcessingErrorModal
        errors={fileProcessingErrors}
        close={clearErrors}
        forceProcess={() => forceProcessFile(fileMetadata?.fileId)}
      />
    </GridContainer>
  );
};

export default UploadedFiles;
